import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { usePurchaseNotices } from './PurchaseNotice';

type PurchaseNotice = { id: number; content: string; expiresAt: number };
const NOTICE_DURATION = 10000;
const SUCCESS_DURATION = 3200;
const MAX_NOTICES = 10;

function purchaseBuyer(content: string) {
  return content.trim().match(/^"?(.+?)"?\s*님(?:이)?\s*구매하셨습니다\.?$/)?.[1];
}

// Only the confirmed order response triggers personal purchase effects.
export function useViewerPurchaseFeedback(liveSeq?: string, viewerNickname?: string, urgentNotice = false) {
  const { notices, showPurchaseNotice: enqueueNotice, clearNotices } = usePurchaseNotices(urgentNotice ? 6 : MAX_NOTICES, NOTICE_DURATION);
  const [celebration, setCelebration] = useState(0);
  const nextId = useRef(0);

  useEffect(() => {
    clearNotices();
    setCelebration(0);
  }, [liveSeq, clearNotices]);

  const showPurchaseNotice = useCallback((content: string) => {
    if (!content?.trim()) return;
    // The buyer gets the confirmed success card, regardless of whether the chat
    // event arrives before or after the order response.
    if (viewerNickname && purchaseBuyer(content) === viewerNickname) return;
    enqueueNotice(content);
  }, [viewerNickname, enqueueNotice]);

  const celebratePurchase = useCallback(() => {
    const id = ++nextId.current;
    setCelebration(id);
  }, []);

  useEffect(() => {
    if (!celebration) return;
    const timer = window.setTimeout(() => setCelebration(0), SUCCESS_DURATION);
    return () => window.clearTimeout(timer);
  }, [celebration]);

  return { notices, celebration, showPurchaseNotice, celebratePurchase };
}

function PurchaseIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m7 12 3 3 7-7" /><circle cx="12" cy="12" r="10" />
  </svg>;
}

export function ViewerPurchaseNotices({ notices, celebration, viewerNickname, urgentNotice = false }: {
  notices: PurchaseNotice[]; celebration: number; viewerNickname?: string; urgentNotice?: boolean;
}) {
  return <NoticeStack>
    <div role="status" aria-live="polite" aria-atomic="true">
      {celebration > 0 && <NoticeRow key={celebration}>
        <PurchaseIcon />
        <span><b>{viewerNickname?.trim() && <>{viewerNickname.trim()}님, </>}득템 성공!</b><small>주문이 완료되었어요.</small></span>
      </NoticeRow>}
    </div>
    <OtherNotices role="log" aria-label="구매 완료 알림" aria-live="polite" aria-relevant="additions text">
      {notices.slice(0, urgentNotice ? 6 : MAX_NOTICES).map(notice => <div key={notice.id}>{notice.content}</div>)}
    </OtherNotices>
  </NoticeStack>;
}

const enterNotice = keyframes`
  from { opacity: 0; transform: translateX(14px); }
  to { opacity: 1; transform: translateX(0); }
`;
const NoticeStack = styled.div`
  position: absolute;
  z-index: 46;
  top: 0;
  right: 16px;
  max-width: calc(100% - 32px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  pointer-events: none;
  > div:empty { display: none; }
`;
const OtherNotices = styled.div`
  color: #54a8ff;
  font-family: var(--font-Pretendard-SemiBold);
  font-size: 15px;
  line-height: 1.3;
  text-align: right;
  white-space: nowrap;
  text-shadow: 0 1px 3px #000;
  > div { max-width: 100%; padding: 2px 0; overflow: hidden; text-overflow: ellipsis; }
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  max-width: 100%;
  @media (max-height: 740px) { font-size: 14px; line-height: 1.25; gap: 0; > div { padding: 1px 0; } }
`;
const NoticeRow = styled.div`
  box-sizing: border-box;
  max-width: min(260px, 100%);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid rgba(52,211,153,.38);
  background: rgba(12,28,26,.86);
  color: #fff;
  font-family: var(--font-Pretendard-Medium);
  font-size: 12px;
  line-height: 1.5;
  text-shadow: 0 1px 3px #000, 0 0 6px rgba(0,0,0,.6);
  overflow-wrap: anywhere;
  animation: ${enterNotice} 280ms ease-out both;
  > svg { flex-shrink: 0; color: #6ee7b7; }
  b { display: block; font-size: 13px; color: #a7f3d0; }
  small { display: block; font-size: 12px; color: #e2e8f0; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
